import { browserTimezone, RecurrenceWithRepeat } from "@gigpro/utils/datetime";
import { eventSchema } from "api/clients/event";
import { z } from "zod";
import { locationAddressSchema } from "./locationAddress";
import { makeApiResource } from "./resource";
import { billingSourceSchema, gigAttachmentSchema, gigStatusSchema, gigTypeSchema } from "./shared";
import { requirementSchema } from "./skills";

const gigBaseSchema = z.object({
  status: gigStatusSchema,
  start_time: z.coerce.date(),
  end_time: z.coerce.date(),
  allowed_confirmed_pros: z.number(),

  skill_id: z.number(),

  location_address_id: z.number().nullish(),
  location_id: z.number(),
  address_line_1: z.string(),
  city: z.string(),
  zip_code: z.string(),
  state_code: z.string(),
  use_location_address: z.boolean(),

  contact_name: z.string(),
  contact_phone: z.string().transform((phone) => (phone.startsWith("+1") ? phone : `+1${phone}`)),

  description: z.string(),
  no_experience_required: z.boolean(),
  special_inst: z.string().optional().nullable(),
  wage_amount: z.coerce.number(),

  type: gigTypeSchema.default("MARKETPLACE"),

  auto_select_enabled: z.boolean().nullish(),
  attachments: z.array(gigAttachmentSchema).nullish(),
  unpaid_break_minutes: z.number().nullish(),
});

export const gigPostPayloadSchema = gigBaseSchema.extend({
  timezone: z.string(),
  selected_requirement_ids: z.array(z.number()).optional(),
  recurrence: RecurrenceWithRepeat.optional(),
  event_id: z.string().optional(),
  attachment_ids: z.array(z.string()).optional(),
  is_auto_post_network_to_marketplace_enabled: z.boolean().optional(),
});
export type GigPostPayload = z.infer<typeof gigPostPayloadSchema>;

const gigSettingSchema = z.object({ display_name: z.string(), description: z.string() });

export const gigSchema = gigBaseSchema.extend({
  id: z.number(),
  created_ts: z.coerce.date(),
  location: makeApiResource({
    image_public_id: z.string().nullish(),
    market_id: z.number(),
    name: z.string(),
    timezone: z.string().catch(browserTimezone),
    billing_source: billingSourceSchema,
    organization_id: z.string(),
  }),
  skill: z.string(),
  skill_id: z.number(),
  requirements: z.array(requirementSchema).optional(),
  background_check_required: z.boolean().default(false),
  background_checks_fee_percentage: z.number().nullish(),
  chat_token: z.string().nullish(),
  bundle_id: z.number().nullish(),
  number_of_gigs_in_bundle: z.number().nullish(),
  auto_select_enabled: z.boolean().nullish(),
  is_recurring: z.boolean().nullish(),
  boosted_gig_bonus: z.coerce.number().nullish(),
  event: eventSchema.nullish(),
  require_certifications: z.boolean().default(false),

  headshots: z.array(z.string()).nullish(),
  checkin_code: z.string(),
  location_address: locationAddressSchema,

  stats: z
    .object({
      total_applications_count: z.number(),
      selected_applications_count: z.number(),
      confirmed_applications_count: z.number(),
      confirmed_auto_matched_pros_count: z.number(),
      cancelled_applications_count: z.number(),
      total_payments_count: z.number(),
      failed_payments_count: z.number(),
      error_payments_count: z.number(),
      submitted_payments_count: z.number(),
      confirmed_pros_count: z.number(),
      paid_pros_count: z.number(),
    })
    .partial(),
  settings: z.object({
    is_checkin_required: gigSettingSchema,
    auto_select_enabled: gigSettingSchema,
    is_auto_post_network_to_marketplace_enabled: gigSettingSchema,
  }),
});

export type Gig = z.infer<typeof gigSchema>;

export const offerableGigSchema = gigSchema
  .pick({ id: true, start_time: true, end_time: true, wage_amount: true, description: true, skill_id: true })
  .extend({
    skill: z.string(),
    location: gigSchema.shape.location.pick({ id: true, image_public_id: true, name: true, timezone: true }),
    has_applied: z.boolean(),
    is_selected: z.boolean().nullable(),
    is_confirmed: z.boolean().nullable(),
    is_cancelled: z.boolean().nullable(),
    is_filled: z.boolean().optional().nullable(),
  });
export type OfferableGig = z.infer<typeof offerableGigSchema>;
